// Import react elements
import { useState } from "react";

// Import api calls
import { 
    updateConnectionSettings,
} from "api/APIStrichCalls"; 

// Import global hooks
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";

// Import utility functions
import { ToTitleCase } from "global/utilities/ConvertCasing";
import { IsEmailValue } from "global/utilities/CheckData";





// This checks whether a connection setting has changed
const HasSettingChanged = (originalValue, currentValue) => {

    // Convert the original value safely
    const safeOriginalValue = originalValue ?? "";

    // Convert the current value safely
    const safeCurrentValue = currentValue ?? "";

    // Return whether the values are different
    return String(safeOriginalValue) !== String(safeCurrentValue);

};



const RenderConnectionSettings = ({connectionDetails, addingNewConnection, formValues, setFormValues, token, fetchConnections}) => {
    

    // Define toast options
    const { toastSuccess, toastError } = useToast();

    // This stores which setting types are currently expanded
    const [openSettingTypes, setOpenSettingTypes] = useState({});

    

    // This renders the small form used to add a new custom store setting.
    function StoreSettingsEntryForm({ handleCreateSetting, isSubmitting = false, initialType = '', initialCode = '', initialName = '', initialDescription = '', initialValue = '', initialValueType = '' }) {

        // Store the setting type input.
        const [configType, setConfigType] = useState(initialType);

        // Store the setting code input.
        const [configCode, setConfigCode] = useState(initialCode);

        // Store the setting name input.
        const [configName, setConfigName] = useState(initialName);

        // Store the setting description input.
        const [configDescription, setConfigDescription] = useState(initialDescription);

        // Store the setting value input.
        const [configValue, setConfigValue] = useState(initialValue);

         // Store the setting value input.
        const [configValueType, setConfigValueType] = useState(initialValueType);

        // State for inline validation messages
        const [errors, setErrors] = useState({ type: '', code: '', name: '', value: '', valueType: '' });


        // Basic validation helper - Ben will add advanced sanitising to the api call?
        const validate = () => {

            // Start with empty errors
            const next = { type: '', code: '', name: '', value: '', valueType: '' };

            // Trim both fields for safety
            const typeTrim = configType.trim();
            const codeTrim = configCode.trim();
            const nameTrim = configName.trim();
            const valueTrim = configValue.trim();
            const valueTypeTrim = configValueType.trim();

            // Require non-empty type
            if (!typeTrim) next.type = 'Type is required';

            // Require non-empty code
            if (!codeTrim) next.code = 'Code is required';

            // Require non-empty name
            if (!nameTrim) next.name = 'Name is required';

            // Require non-empty value
            if (!valueTrim) next.value = 'Value is required';

            // Require non-empty value type
            if (!valueTypeTrim) next.valueType = 'Value Type is required';

            // Example rule: prevent leading/trailing spaces inside code (teaches intent)
            if (codeTrim !== configCode && !next.code) next.code = 'Code cannot start/end with spaces';

            // Update errors state
            setErrors(next);

            // Return validity boolean
            return !next.type && !next.code && !next.name && !next.value && !next.valueType;

        };


        // Handle form submission
        const handleSubmit = async () => {

            // Validate before preparing payload
            if (!validate()) return;

            // Prepare a stable payload shape (array to allow future multi-add)
            const payload = {

                connection_id: connectionDetails.id,
                type: configType.trim(),
                code: configCode.trim(),
                name: configName.trim(),
                description: configDescription.trim(),
                value: configValue.trim(),
                value_type: configValueType.trim(),
            
            };

            // Call parent submit handler if provided
            if (typeof handleCreateSetting === 'function') {

                await handleCreateSetting(payload, true);

            }

        };


        // Render controlled inputs + helper text
        return (

            <div className="config-form">

                {/* Type input row */}
                <div className="config-form__row">

                    <label className="config-form__label" htmlFor="config-key">Setting Type: </label>

                    <input
                        id="config-type"
                        name="configType"
                        className={`config-form__input ${errors.type ? 'config-form__input--error' : ''}`}
                        type="text"
                        value={configType}
                        onChange={(e) => setConfigType(e.target.value)}
                        placeholder="e.g. Store"
                        autoComplete="off"
                        disabled={isSubmitting}
                        style={{width: "auto"}}
                    />

                    {errors.type && <div className="config-form__error" role="alert">{errors.type}</div>}

                </div>

                {/* Code input row */}
                <div className="config-form__row">

                    <label className="config-form__label" htmlFor="config-key">Code: </label>

                    <input
                        id="config-code"
                        name="configCode"
                        className={`config-form__input ${errors.code ? 'config-form__input--error' : ''}`}
                        type="text"
                        value={configCode}
                        onChange={(e) => setConfigCode(e.target.value)}
                        placeholder="e.g. background_colour"
                        autoComplete="off"
                        disabled={isSubmitting}
                        style={{width: "auto"}}
                    />

                    {errors.code && <div className="config-form__error" role="alert">{errors.code}</div>}

                </div>

                {/* Name input row */}
                <div className="config-form__row">

                    <label className="config-form__label" htmlFor="config-key"> Name: </label>

                    <input
                        id="config-name"
                        name="configName"
                        className={`config-form__input ${errors.name ? 'config-form__input--error' : ''}`}
                        type="text"
                        value={configName}
                        onChange={(e) => setConfigName(e.target.value)}
                        placeholder="e.g. Background Colour"
                        autoComplete="off"
                        disabled={isSubmitting}
                        style={{width: "auto"}}
                    />

                    {errors.name && <div className="config-form__error" role="alert">{errors.name}</div>}

                </div>

                {/* Description input row */}
                <div className="config-form__row">

                    <label className="config-form__label" htmlFor="config-key">Description: </label>

                    <input
                        id="config-description"
                        name="configDescription"
                        className={`config-form__input ${errors.description ? 'config-form__input--error' : ''}`}
                        type="text"
                        value={configDescription}
                        onChange={(e) => setConfigDescription(e.target.value)}
                        placeholder="e.g. This is the background colour for the store website."
                        autoComplete="off"
                        disabled={isSubmitting}
                        style={{width: "auto"}}
                    />

                    {errors.description && <div className="config-form__error" role="alert">{errors.description}</div>}

                </div>

                
                {/* Value Type input row */}
                <div className="config-form__row">

                    <label className="config-form__label" htmlFor="config-key">Value Type: </label>

                    <input
                        id="config-valueType"
                        name="configValueType"
                        className={`config-form__input ${errors.valueType ? 'config-form__input--error' : ''}`}
                        type="text"
                        value={configValueType}
                        onChange={(e) => setConfigValueType(e.target.value)}
                        placeholder="e.g. colour, number, font"
                        autoComplete="off"
                        disabled={isSubmitting}
                        style={{width: "auto"}}
                    />

                    {errors.valueType && <div className="config-form__error" role="alert">{errors.valueType}</div>}

                </div>

                {/* Value input row */}
                <div className="config-form__row">

                    <label className="config-form__label" htmlFor="config-value">Value: </label>

                    <input
                        id="config-value"
                        name="configValue"
                        className={`config-form__input ${errors.value ? 'config-form__input--error' : ''}`}
                        type="text"
                        value={configValue}
                        onChange={(e) => setConfigValue(e.target.value)}
                        placeholder="e.g. #cdcdcd"
                        autoComplete="off"
                        disabled={isSubmitting}
                        style={{width: "auto"}}
                    />

                    {errors.value && <div className="config-form__error" role="alert">{errors.value}</div>}

                </div>

                {/* Submit row */}
                <div className="config-form__actions">

                    <CreateButton
                        title={isSubmitting ? "Saving…" : "Add Setting"}
                        type="button"
                        size="s"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                    />

                </div>

            </div>

        );

    }


    
    // This groups settings by type for the settings card.
    const groupSettingsByType = (settingsArray) => {

        // Reduce the array into grouped object
        return settingsArray.reduce((groupedSettings, setting) => {

            // Get the type or fallback to "unknown"
            const settingType = setting.type || "unknown";

            // Create the array for this type if it does not exist
            if (!groupedSettings[settingType]) {

                groupedSettings[settingType] = [];

            }

            // Push the setting into the correct type array
            groupedSettings[settingType].push(setting);

            // Return the updated grouped object
            return groupedSettings;

        }, {});

    };
    
    // Group either the draft settings or the saved fallback settings before rendering the Store Settings card.
    const groupedSettings = groupSettingsByType(formValues.settings || connectionDetails?.settings || []);



    // This opens or closes one setting type section.
    const handleToggleSettingTypeOpen = (type) => {

        // Update open state using previous state safely
        setOpenSettingTypes((previousOpenSettingTypes) => ({

            // Keep all previous open states
            ...previousOpenSettingTypes,

            // Toggle the current shipping method
            [type]: !previousOpenSettingTypes[type],

        }));

    };



    // This inserts or replaces one draft setting row inside formValues.settings while preserving saved metadata when available.
    const buildUpdatedSettings = (settingsArray = [], {
        type,
        code,
        name,
        description,
        value,
        value_type,
    }) => {

        // Normalise the current settings array so we can safely search and update it.
        const existingSettings = Array.isArray(settingsArray) ? settingsArray : [];

        // Prefer the draft copy of the setting when it already exists in the form state.
        const existingDraftSettingByCode = existingSettings.find((setting) => setting.code === code);

        // Fall back to the saved connection setting if the draft version is not present yet.
        const existingSavedSettingByCode = connectionDetails?.settings?.find((setting) => setting.code === code);

        // Reuse whatever metadata we already know about this setting before applying the new value.
        const fallbackSetting =
            existingDraftSettingByCode
            || existingSavedSettingByCode
            || {};

        // Build the next canonical setting object so both text inputs and preview settings update the same shape.
        const nextSetting = {
            ...fallbackSetting,
            type: fallbackSetting.type || type || "style",
            code,
            name: fallbackSetting.name || name || code,
            description: fallbackSetting.description || description || "",
            value,
            value_type,
        };

        // Check whether this setting already exists in the current draft state.
        const settingExists = existingSettings.some((setting) => setting.code === code);

        // Replace the existing setting when present, otherwise append a brand new row.
        return settingExists
            ? existingSettings.map((setting) => {
                if (setting.code !== code) {
                    return setting;
                }

                return {
                    ...setting,
                    ...nextSetting,
                };
            })
            : [...existingSettings, nextSetting];
    };



    // This applies one setting update to the form state so unsaved preview/settings edits stay in sync across the popup.
    const upsertDraftSetting = (settingUpdate) => {

        setFormValues((previousValues) => ({
            ...previousValues,
            settings: buildUpdatedSettings(previousValues?.settings, settingUpdate),
        }));

    };



    // This updates one draft setting from a normal input change event.
    const handleSettingsChange = (type, code, name = "", description = "", value_type ) => (e) => {

        // Read the raw input value from the event
        const rawValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        // Save the new value into the draft settings array using the shared upsert helper.
        upsertDraftSetting({
            type,
            code,
            name,
            description,
            value: rawValue,
            value_type,
        });

    };


    // This checks whether a value is a valid CSS colour, including named colours like "red"
    const IsValidCssColour = (value) => {

        // Ensure we only work with strings
        if (typeof value !== "string") {

            // Not a valid colour input
            return false;

        }

        // Trim the incoming value
        const trimmedValue = value.trim();

        // Empty strings are not colours
        if (!trimmedValue) {

            // Return false for empty values
            return false;

        }

        // Create a temporary DOM element for browser-native colour parsing
        const testElement = document.createElement("div");

        // Clear existing colour
        testElement.style.color = "";

        // Try to assign the provided value
        testElement.style.color = trimmedValue;

        // If the browser accepted the value it will not remain empty
        return testElement.style.color !== "";

    };



    // This checks whether a value looks like a gradient string
    const IsGradientValue = (value) => {

        // Ensure we only work with strings
        if (typeof value !== "string") {

            // Non-strings cannot be gradients
            return false;

        }

        // Trim the value and test common CSS gradient formats
        return /^(linear-gradient|radial-gradient|conic-gradient)\(/i.test(value.trim());

    };



    // This checks whether a value looks like an image URL
    const IsImageUrl = (value) => {

        // Ensure we only work with strings
        if (typeof value !== "string") {

            // Non-strings cannot be image URLs
            return false;

        }

        // Match common image file extensions
        return /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|avif|bmp)(\?.*)?$/i.test(value.trim());

    };



    // This checks whether a value looks like any URL
    const IsUrl = (value) => {

        // Ensure we only work with strings
        if (typeof value !== "string") {

            // Non-strings cannot be URLs
            return false;

        }

        // Match http and https URLs
        return /^https?:\/\/.+/i.test(value.trim());

    };



    // This checks whether a value looks boolean-like
    const GetBooleanLikeValue = (value) => {

        // Convert incoming value to a normalised lowercase string
        const normalisedValue = String(value ?? "").trim().toLowerCase();

        // Detect "true-like" values
        if (["true", "1", "y", "yes", "on"].includes(normalisedValue)) {

            // Return true-like
            return "true";

        }

        // Detect "false-like" values
        if (["false", "0", "n", "no", "off"].includes(normalisedValue)) {

            // Return false-like
            return "false";

        }

        // Not boolean-like
        return null;

    };



    // This checks whether a value looks like a number or size token
    const GetNumberOrSizeType = (value) => {

        // Convert to string for consistent checking
        const stringValue = String(value ?? "").trim();

        // Empty values do not count
        if (!stringValue) {

            // Return null for no match
            return null;

        }

        // Detect a plain number, including decimals and negatives
        if (/^-?\d+(\.\d+)?$/.test(stringValue)) {

            // Return number type
            return "number";

        }

        // Detect common CSS / UI measurement units
        if (/^-?\d+(\.\d+)?(px|rem|em|%|vh|vw|vmin|vmax|pt|cm|mm|in)$/i.test(stringValue)) {

            // Return size type
            return "size";

        }

        // No number-like type found
        return null;

    };



    // This renders a simple preview for a setting value.
    const StoreSettingPreview = ({ value }) => {

        // Convert the incoming value into a safe display string
        const safeValue = String(value ?? "").trim();

        // Detect whether the value is boolean-like
        const booleanLikeValue = GetBooleanLikeValue(safeValue);

        // Detect whether the value is a number or size
        const numberOrSizeType = GetNumberOrSizeType(safeValue);

        // Check whether this is a gradient
        const isGradientValue = IsGradientValue(safeValue);

        // Check whether this is a valid CSS colour
        const isValidCssColour = IsValidCssColour(safeValue);

        // Check whether this is an image URL
        const isImageUrl = IsImageUrl(safeValue);

        // Check whether this is a general URL
        const isUrl = IsUrl(safeValue);

        // Check whether this is an email
        const isEmailValue = IsEmailValue(safeValue);

        // Render a boolean badge if value is boolean-like
        if (booleanLikeValue === "true") {

            // Return a positive badge
            return (

                <div className="storeSettingPreview storeSettingPreview--booleanTrue" title={safeValue}>

                    ON

                </div>

            );

        }

        // Render a false badge if value is boolean-like false
        if (booleanLikeValue === "false") {

            // Return a negative badge
            return (

                <div className="storeSettingPreview storeSettingPreview--booleanFalse" title={safeValue}>

                    OFF

                </div>

            );

        }

        // Render an image preview for image URLs
        if (isImageUrl) {

            // Return the image preview
            return (

                <div className="storeSettingPreview storeSettingPreview--image" title={safeValue}>

                    <img
                        className="storeSettingPreview__image"
                        src={safeValue}
                        alt="Setting preview"
                    />

                </div>

            );

        }

        // Render a gradient preview
        if (isGradientValue) {

            // Return a gradient swatch using the value as the background
            return (

                <div className="storeSettingPreview storeSettingPreview--gradient" title={safeValue}>

                    <span
                        className="storeSettingPreview__gradientSwatch"
                        style={{ background: safeValue }}
                    />

                </div>

            );

        }

        // Render a colour preview for valid CSS colours
        if (isValidCssColour) {

            // Return a colour dot
            return (

                <div className="storeSettingPreview storeSettingPreview--colour" title={safeValue}>

                    <span
                        className="storeSettingPreview__colourDot"
                        style={{ backgroundColor: safeValue }}
                    />

                </div>

            );

        }

        // Render an email badge
        if (isEmailValue) {

            // Return an email badge
            return (

                <div className="storeSettingPreview storeSettingPreview--email" title={safeValue}>

                    @

                </div>

            );

        }

        // Render a number or size badge
        if (numberOrSizeType === "number") {

            // Return a number badge
            return (

                <div className="storeSettingPreview storeSettingPreview--number" title={safeValue}>

                    123

                </div>

            );

        }

        // Render a size badge
        if (numberOrSizeType === "size") {

            // Return a size badge
            return (

                <div className="storeSettingPreview storeSettingPreview--size" title={safeValue}>

                    px

                </div>

            );

        }

        // Render a generic URL badge for non-image URLs
        if (isUrl) {

            // Return a URL badge
            return (

                <div className="storeSettingPreview storeSettingPreview--url" title={safeValue}>

                    URL

                </div>

            );

        }

        // Fall back to a generic text badge
        return (

            <div className="storeSettingPreview storeSettingPreview--text" title={safeValue}>

                Aa

            </div>

        );

    };



    // This saves one store setting and then reloads the connection.
    const handleCreateSetting = async (payload, isAddingNewSetting) => {

        // Update connection with new setting payload
        const response = await updateConnectionSettings(token, payload);

        if (response.success) {

            // Refetch connections
            await fetchConnections(connectionDetails.id);

            // Toast success
            toastSuccess({ title: "Success", message: isAddingNewSetting ? "Setting added." : "Setting updated." });

        } else {

            // Create error message
            const errorMessage = `Could not ${isAddingNewSetting ? 'add' : 'update'} setting: ${response.error_message ? response.error_message : "Please try again later."}`

            // Toast error message
            toastError({ title: "Error", message: errorMessage, autoCloseMs: 6000 });

        }

    };



    // This saves one individual setting immediately from the settings list Save button.
    const updateSetting = async (type, code, name, description, value, value_type) => {

        // Prepare a stable payload shape (array to allow future multi-add)
        const payload = {

            connection_id: connectionDetails.id,
            type: type,
            code: code,
            name: name,
            description: description,
            value_type: value_type,
            value: value,
                    
        };

        // Call parent submit handler if provided
        if (typeof handleCreateSetting === 'function') {

            await handleCreateSetting(payload, false);

        }

    };



    return (

        // This is the Store Settings card 
        <div className="card">

            <div className="card__header">

                <h3>Store Settings</h3>

                <span className="hintText">

                    Change the look and feel of your store

                </span>

            </div>

            <div className="card__content">
                
                {/* Tell the user they need to save the store before settings will appear */}
                {addingNewConnection &&
                
                    <div>
                        <p>Click save to view and edit settings.</p>
                    </div>
                
                }

                {/* Display all setting types */}
                <div className="storeSettingTypes">

                    {/* Display type settings */}
                    {connectionDetails?.conntype === "STRICH" && formValues.settings &&
                        
                        Object.entries(groupedSettings).map(([type, settingsArray]) => {

                            // This checks whether the current method is expanded
                            const isOpen = !!openSettingTypes[type];

                            return (

                                <div key={type} className="shipping-methods-list__card">

                                    <div
                                        className="settings-list-header horizontal-align-content-center flex-justify-left-right" 
                                        onClick={() => handleToggleSettingTypeOpen(type)}
                                    >

                                        <div>

                                            <h2>{ToTitleCase(type)}</h2>

                                        </div>

                                        <div>

                                            {/* This is the expand and collapse icon */}
                                            <span className="shipping-methods-list__open-close">
                                                {isOpen ? "-" : "+"}
                                            </span>
                            
                                        </div>

                                    </div>
                                    
                                    {isOpen &&

                                        <div className="storeSettingsList">

                                            {/* Loop through settings that are not null */}
                                            {settingsArray.map((setting) => {
                                                
                                                    // Read the original setting value safely
                                                    const originalSetting = connectionDetails?.settings?.find((s) => s.type === setting.type && s.code === setting.code) ?? "";

                                                    // Check whether the current value differs from the original value
                                                    const isEdited = HasSettingChanged(originalSetting.value, setting.value);

                                                    // Render a row for this setting
                                                    return (

                                                        <div
                                                            key={setting.code}
                                                            className={`storeSetting ${isEdited ? "storeSetting--edited" : ""}`}
                                                        >

                                                            {/* This is the left side with the setting key */}
                                                            <div className="storeSetting__left" title={setting.description}>

                                                                <div className="storeSetting__key">

                                                                    {setting.name}

                                                                </div>

                                                            </div>

                                                            {/* This is the middle editable value area */}
                                                            <div className="storeSetting__middle" title={setting.code}>

                                                                <input
                                                                    name={setting.code}
                                                                    type="text"
                                                                    value={setting.value ?? ""}
                                                                    onChange={handleSettingsChange(setting.type, setting.code)}
                                                                />

                                                            </div>

                                                            {/* This is the right side with preview and remove button */}
                                                            <div className="storeSetting__right">

                                                                <StoreSettingPreview value={setting.value} />

                                                                {isEdited &&
                                                                
                                                                <CreateButton
                                                                    title="Save"
                                                                    size="s"
                                                                    onClick={() => updateSetting(setting.type, setting.code, setting.name, setting.description, setting.value, setting.value_type)}
                                                                />

                                                                }

                                                            </div>

                                                        </div>

                                                    );

                                                })}

                                        </div>

                                    }

                                </div>
                        
                            )

                        })
                        
                    }

                </div>

                {/* Display inputs to add new store settings if this is a STRICH connection */}
                {connectionDetails?.conntype === "STRICH" && 
                
                    <div>

                        <StoreSettingsEntryForm handleCreateSetting={handleCreateSetting} />

                    </div>

                }
                
            </div>
            
        </div>

    );

}; export default RenderConnectionSettings;
